import { Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import type { ReactNode } from 'react';

import type { SearchHit } from '../../services/searchApi';
import { effectiveCpr } from '../../utils/pricing';
import { Cartridge } from '../brand/Cartridge';
import { StockPill } from '../brand/StockPill';
import { FreshnessChip } from '../brand/FreshnessChip';

export interface ListingCardProps {
  hit: SearchHit;
  variant?: 'compact' | 'full';
}

function formatPrice(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)}`;
}

function formatCPR(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(3)}`;
}

interface CardLinkProps {
  hit: SearchHit;
  children: ReactNode;
}

/**
 * Resolve the target the card links to:
 *   - prefer `/products/{productSlug}` (canonical SEO URL, ADR 0004),
 *   - fall back to `/products/{productId}`,
 *   - last resort: the listing's external `sourceUrl` (new tab + noopener).
 */
function CardLink({ hit, children }: CardLinkProps) {
  const productPath = hit.productSlug
    ? `/products/${hit.productSlug}`
    : hit.productId
      ? `/products/${hit.productId}`
      : null;
  if (productPath) {
    return (
      <CardActionArea component={RouterLink} to={productPath} sx={{ height: '100%' }}>
        {children}
      </CardActionArea>
    );
  }
  if (hit.sourceUrl) {
    return (
      <CardActionArea
        component="a"
        href={hit.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${hit.title ?? 'Listing'} — opens in a new tab`}
        sx={{ height: '100%' }}
      >
        {children}
      </CardActionArea>
    );
  }
  return <Box sx={{ p: 2, height: '100%' }}>{children}</Box>;
}

/**
 * Product/listing card (tactical styling). Used on the home page (compact) and
 * search results (full). Surfaces the title, brand/caliber, $/round + delivered
 * price, stock pill, data-freshness, retailer, and — when applicable — a clearly
 * labeled Sponsored badge (spec §23: sponsored ≠ organic).
 */
export function ListingCard({ hit, variant = 'compact' }: ListingCardProps) {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const isFull = variant === 'full';
  const caliber = hit.attributes.caliber ?? null;
  const grain =
    hit.attributes.bulletWeightGrains != null ? `${hit.attributes.bulletWeightGrains}gr` : null;
  const metaBits = [
    grain,
    hit.attributes.bulletType,
    hit.roundCount ? `box/${hit.roundCount}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: t.panel,
        borderColor: hit.isSponsored
          ? theme.palette.warning.dark
          : hit.isPromoted
            ? theme.palette.info.dark
            : t.line,
        borderWidth: hit.isSponsored || hit.isPromoted ? 2 : 1,
        overflow: 'hidden',
        transition: 'border-color 120ms ease',
        '&:hover': {
          borderColor: hit.isSponsored
            ? theme.palette.warning.main
            : hit.isPromoted
              ? theme.palette.info.main
              : t.lineHi,
        },
      }}
    >
      <CardLink hit={hit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Thumb header: cartridge silhouette + caliber + stock */}
          <Box
            sx={{
              position: 'relative',
              height: isFull ? 96 : 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${t.line}`,
              background: `repeating-linear-gradient(135deg, ${t.panelHi} 0, ${t.panelHi} 6px, ${t.panel} 6px, ${t.panel} 12px)`,
            }}
          >
            <Cartridge
              caliber={caliber}
              ammoType={hit.attributes.ammoType}
              height={isFull ? 44 : 56}
              decorative
            />
            {caliber && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: t.bg,
                  border: `1px solid ${t.line}`,
                  fontFamily: t.monoFont,
                  fontSize: 10,
                  color: t.dim,
                  letterSpacing: '0.06em',
                }}
              >
                {caliber}
              </Box>
            )}
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <StockPill status={hit.stockStatus} size="sm" />
            </Box>
            {hit.isSponsored && (
              <Chip
                label="Sponsored"
                size="small"
                aria-label="Sponsored"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  height: 20,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontFamily: t.monoFont,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              />
            )}
            {/* Promoted-retailer badge (#149, §23). Visually distinct from the
                Sponsored chip (info vs warning) and programmatically labeled so
                AT users can tell promoted from organic. Only shown when the hit
                isn't also a paid sponsored slot. */}
            {!hit.isSponsored && hit.isPromoted && (
              <Chip
                label="Promoted"
                size="small"
                aria-label="Promoted"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  height: 20,
                  bgcolor: theme.palette.info.main,
                  color: theme.palette.info.contrastText,
                  fontFamily: t.monoFont,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              />
            )}
          </Box>

          {/* Body */}
          <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography
              variant={isFull ? 'h4' : 'body1'}
              component="p"
              sx={{
                fontWeight: 600,
                color: t.text,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.25,
              }}
            >
              {hit.title ?? 'Untitled listing'}
            </Typography>

            {metaBits && (
              <Typography sx={{ fontFamily: t.monoFont, fontSize: 11, color: t.dim }}>
                {metaBits}
              </Typography>
            )}

            <Box
              sx={{
                mt: 'auto',
                pt: 1,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: t.monoFont,
                    fontSize: 9,
                    color: t.dim,
                    letterSpacing: '0.12em',
                  }}
                >
                  $/RD
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: isFull ? '1.6rem' : '1.4rem',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    color: t.accent,
                  }}
                >
                  {formatCPR(effectiveCpr(hit))}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontFamily: t.monoFont, fontSize: 11, color: t.text }}>
                  {formatPrice(hit.price)}
                </Typography>
                {hit.roundCount ? (
                  <Typography sx={{ fontFamily: t.monoFont, fontSize: 10, color: t.dim }}>
                    {hit.roundCount} rds
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Box
              sx={{
                mt: 0.5,
                pt: 1,
                borderTop: `1px solid ${t.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {hit.retailer?.name ? (
                <Typography sx={{ fontSize: 12, color: t.text }}>{hit.retailer.name}</Typography>
              ) : (
                <span />
              )}
              <FreshnessChip
                freshness={hit.dataFreshness}
                lastUpdated={hit.lastUpdated}
                testId="freshness-chip"
              />
            </Box>
          </Box>
        </Box>
      </CardLink>
    </Card>
  );
}

export default ListingCard;
